/**
 * ScreenShareAnimation - Animated component for screen sharing events
 * Shows divine messenger sharing visions from the heavens
 */

import { useEffect, useState, useRef } from 'react';

interface ScreenShareAnimationProps {
  username: string;
  isStarting: boolean; // true for start, false for stop
  onComplete?: () => void;
}

export default function ScreenShareAnimation({
  username,
  isStarting,
  onComplete,
}: ScreenShareAnimationProps) {
  const [isVisible, setIsVisible] = useState(true);
  const onCompleteRef = useRef(onComplete);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    // Show animation for 2 seconds
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(() => onCompleteRef.current?.(), 300);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div
      className={`fixed top-4 right-4 z-50 transition-all duration-300 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'
      }`}
    >
      <div className="max-w-sm relative overflow-hidden">
        {/* Rainbow gradient border animation */}
        <div
          className="absolute inset-0 opacity-20 animate-rainbow"
          style={{
            background:
              'linear-gradient(90deg, #9333ea, #3b82f6, #06b6d4, #10b981, #f59e0b, #9333ea, #3b82f6)',
          }}
        />

        {/* Content */}
        <div className="relative z-10 flex items-center gap-4 p-4">
          {/* Animated icon */}
          <div className="relative w-16 h-16 flex-shrink-0">
            {/* Pulsing background */}
            <div className="absolute inset-0 bg-gradient-to-br from-brand-tertiary/20 to-brand-secondary/20 rounded-lg animate-pulse-slow" />

            {/* Screen icon with shimmer */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="relative">
                {/* Monitor outline */}
                <div className="w-12 h-9 border-3 border-brand-tertiary rounded-md relative animate-shimmer">
                  {/* Screen content with rainbow effect */}
                  <div
                    className="absolute inset-1 rounded-sm opacity-60 animate-rainbow"
                    style={{
                      background:
                        'linear-gradient(135deg, #9333ea, #3b82f6, #06b6d4, #10b981, #f59e0b)',
                    }}
                  />

                  {/* Golden sparkles */}
                  <div className="absolute -top-1 -right-1 w-2 h-2 bg-brand-primary rounded-full animate-ping" />
                  <div
                    className="absolute -bottom-1 -left-1 w-1.5 h-1.5 bg-brand-secondary rounded-full animate-ping"
                    style={{ animationDelay: '0.5s' }}
                  />
                </div>

                {/* Monitor stand */}
                <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-6 h-2 bg-brand-tertiary/60 rounded-b" />

                {/* Iris wings effect */}
                {isStarting && (
                  <>
                    <div
                      className="absolute top-0 -left-2 w-4 h-4 opacity-70 animate-float"
                      style={{
                        background:
                          'radial-gradient(circle, rgba(var(--brand-primary),0.8) 0%, transparent 70%)',
                        filter: 'blur(2px)',
                      }}
                    />
                    <div
                      className="absolute top-0 -right-2 w-4 h-4 opacity-70 animate-float"
                      style={{
                        background:
                          'radial-gradient(circle, rgba(var(--brand-primary),0.8) 0%, transparent 70%)',
                        filter: 'blur(2px)',
                        animationDelay: '0.3s',
                      }}
                    />
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Message */}
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-lg text-brand-primary truncate flex items-center gap-2">
              {isStarting ? '📡 Vision Shared' : '🌙 Vision Ended'}
            </h3>
            <p className="text-brand-on-background/90 text-sm">
              <span className="font-semibold">{username}</span>
              {isStarting ? ' is sharing divine visions' : ' has ceased the transmission'}
            </p>
            <div className="flex gap-1 mt-2">
              {[0, 1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="w-1.5 h-1.5 rounded-full animate-pulse"
                  style={{
                    background: ['#9333ea', '#3b82f6', '#06b6d4', '#10b981', '#f59e0b'][i],
                    animationDelay: `${i * 0.15}s`,
                  }}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Sparkle particles */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className={`absolute w-1 h-1 rounded-full ${
                isStarting ? 'animate-float' : 'animate-pulse'
              }`}
              style={{
                background: ['#9333ea', '#3b82f6', '#06b6d4', '#10b981', '#f59e0b', '#f43f5e'][i],
                left: `${15 + i * 15}%`,
                top: `${10 + (i % 3) * 30}%`,
                animationDelay: `${i * 0.2}s`,
                opacity: 0.7,
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
